import { Component, type ErrorInfo, type ReactNode } from 'react'
import { trackEvent } from '@yoreflix/core/analytics/ga'

/**
 * The last line of defence for every React root in the browser viewer.
 *
 * ★ Why it matters MORE here than in an ordinary SPA. viewermain is a deliberately
 * TRANSPARENT document (viewermain.css forces `background: transparent` on html/body/
 * #root so the z1 video plane composites behind it). React unmounts the whole tree
 * when a render throws — so before this, one bad entry in the feed, one undefined
 * read in a screen, and the viewer became a *see-through empty iframe*. Not a broken
 * page: no page. Nothing on screen said anything had happened, and nothing offered a
 * way out.
 *
 * So the crash screen paints its OWN opaque background — it cannot inherit one — and
 * always offers the single action that helps.
 *
 * ⚠ This catches RENDER, lifecycle and constructor errors only. Errors thrown from an
 * async callback, an event handler or a rejected promise never reach a boundary; those
 * are handled at their own call sites (see CatalogGate for the catalogue's).
 */
interface Props {
  /** Which document this guards — reported to GA, so crashes are countable per surface. */
  surface: 'host' | 'viewermain' | 'viewercontrols' | 'viewershaka' | 'tvlink'
  /**
   * Run once when a crash is caught, before the fallback paints.
   *
   * ★ viewermain uses this to send its `Ready` handshake. Without it a crash during the
   * FIRST render means the handshake component never mounts, the container never reveals
   * z2, and the user sits behind the boot animation for 12s before the host's own
   * NoNetwork timeout fires — i.e. the crash screen would be rendered, correctly, and
   * then hidden behind a curtain. The surface really is "ready"; this is just the outcome.
   */
  onCatch?: () => void
  /**
   * `'screen'` (default) paints the crash screen.
   *
   * `'none'` renders nothing. For a surface that sits BEHIND the others — the z1 video
   * plane — an alert would be both invisible and unreachable, so painting one would only
   * trade a broken picture for a broken picture with a button nobody can press. Those
   * surfaces route the failure through `onCatch` to a layer the user can actually see.
   */
  fallback?: 'screen' | 'none'
  children: ReactNode
}

interface State {
  crashed: boolean
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false }

  static getDerivedStateFromError(): State {
    return { crashed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A crash nobody can count is a crash nobody will fix. GA is a no-op wherever gtag
    // was never initialised (every frame but viewermain), so this is safe from all five.
    trackEvent('app_exception', {
      surface: this.props.surface,
      // GA4 truncates long parameter values; send something diagnosable rather than
      // something complete.
      description: `${error.name}: ${error.message}`.slice(0, 200),
      component_stack: (info.componentStack ?? '').trim().split('\n').slice(0, 3).join(' ').slice(0, 200),
      fatal: true,
    })
    this.props.onCatch?.()
  }

  render() {
    if (!this.state.crashed) return this.props.children
    return this.props.fallback === 'none' ? null : <CrashScreen />
  }
}

/**
 * ⚠ STYLED INLINE, NOT WITH TAILWIND UTILITIES — ON PURPOSE.
 *
 * A fallback that can fail is not a fallback, and the utilities a document has depend on
 * the sheet it loads. `tvlink.html` deliberately does not load `index.css`: it loads on a
 * phone with a code counting down on a television across the room, so its own `tvlink.css`
 * generates ONLY the utilities `TvLink.tsx` uses (since 2026-09-17; before that, none at
 * all). A Tailwind-classed crash screen would render there as unstyled black text on a
 * near-black background — a blank page wearing a hat.
 *
 * The CSS variables come from `tokens.css`, which EVERY entry loads, and each one still
 * carries a literal fallback in case even that is missing.
 *
 * Reload targets the TOP document, so an embedded guest re-boots the whole container
 * rather than reloading itself into the same broken half-state. Standalone — native
 * loads viewermain/viewercontrols as their own webview URLs — `window.top` IS `window`,
 * so the same expression reloads the right thing in both worlds.
 */
function CrashScreen() {
  return (
    <div
      data-testid="crash-screen"
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        // Above every layer this app defines (topbar 60, flip fade 70, starting overlay 100).
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: '0 24px',
        textAlign: 'center',
        background: 'var(--bg-base, #0a120c)',
        color: 'var(--text-primary, #f8f2de)',
        font: 'inherit',
      }}
    >
      <h1 style={{ margin: 0, fontSize: 24, color: 'var(--accent, #eace84)' }}>Something went wrong</h1>
      <p style={{ margin: 0, maxWidth: '24rem', color: 'var(--text-muted, #c0c0c0)' }}>
        Yoreflix hit an unexpected problem. Reloading usually fixes it.
      </p>
      <button
        type="button"
        data-testid="crash-reload"
        onClick={() => (window.top ?? window).location.reload()}
        style={{
          cursor: 'pointer',
          border: 0,
          borderRadius: 9999,
          padding: '8px 24px',
          fontSize: 16,
          fontWeight: 500,
          background: 'var(--primary, #74ac8c)',
          color: 'var(--on-primary, #0a120c)',
        }}
      >
        Reload
      </button>
    </div>
  )
}
